import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { observer } from "@legendapp/state/react";
import { useQuery } from "@tanstack/react-query";
import { useFeatureFlagEnabled, usePostHog } from "posthog-js/react";
import type {
  AppConfigResponse,
  GetLessonDataResponse,
  Lesson,
  UserVideoWord,
  UserWordStatus,
  VideoActivity,
} from "@shared_types";
import { API_BASE_URL } from "@/api/base";
import ContinueButton from "@/components/Buttons/ContinueButton";
import BackButton from "@/ui/BackButton";
import { useUserSettings } from "@/hooks/useUserSettings";
import { useChapterInfo } from "@/hooks/useChapterInfo";
import { isInvalidDictionaryWord } from "@/components/Subtitles/invalidDictionaryWord";
import { getPlatform } from "@/lib/iap";
// Shared video-screen pieces ported from the feed (iOS renders ONE native
// VideoScreenView for both feed and lessons — VideoNew.tsx:424-452). These are
// reused by import; the lesson route installs a single-chapter feed-state shim
// below so their `activeLessonData$`-derived plumbing resolves this lesson.
import { VideoPlayer as FeedVideoPlayer } from "@/feed/features/VideoPlayer";
import { Transcript } from "@/feed/features/Transcript";
import { VIDEO_MAX_W, resolveVideoSource } from "@/feed/features/videoConstants";
import { Scrubber } from "@/feed/components/ui/Scrubber";
import { CenterPlayPause } from "@/feed/components/ui/CenterPlayPause";
import { BottomPanel } from "@/feed/components/ui/BottomPanel";
import { FeedWordlistModal } from "@/feed/features/FeedWordlistModal";
import WordStatesRecapModal from "@/feed/features/WordStatesRecapModal";
import { chapters$, feedCurrentIndex$ } from "@/feed/state/feedState";
import { toggleWordKnown } from "@/feed/lib/wordStatus";
import {
  lesson$,
  lessonDataCache$,
  captionsVisible$,
  panelMode$,
  selectedWord$,
  selectedPhraseGloss$,
  wordStatusOverrides$,
  handlePanelClose,
  isPaused$,
  resetPlayerState,
  videoDuration$,
} from "@/feed/state/videoState";
import { dailyElapsedSeconds$ } from "@/feed/state/userState";
import LessonActionBar from "../components/Video/LessonActionBar";
import LessonTutorialOverlay, {
  type LessonTutorialPhase,
} from "../components/Video/LessonTutorialOverlay";
import { getVideoPropsForDisplayMode } from "../components/Video/videoDisplayMode";
import "../App.css";

// Ordered list of video levels from lowest to highest (must match iOS
// VideoNew.tsx LEVEL_ORDER / useActivities.ts).
const LEVEL_ORDER = [
  "No Level",
  "A0",
  "A0 - Scratch",
  "Lower A1",
  "Upper A1",
  "Lower A2",
  "Upper A2",
  "Lower B1",
  "Upper B1",
  "Lower B2",
  "Upper B2",
  "Lower C1",
  "Upper C1",
  "C2",
];

// Two clear focus lines with the line above and below fading off (iOS
// visibleLines=2 + top/bottom fade overlays). The Transcript mask fades the top
// and bottom edges, so this height leaves ~two legible lines in the middle.
const TRANSCRIPT_EXPANDED_HEIGHT = "95px";
/** Delay (ms) before collapsing transcript height, so clip-path starts first. */
const TRANSCRIPT_COLLAPSE_DELAY_MS = 90;

const transcriptCollapseStyle = (isVisible: boolean): CSSProperties => ({
  height: isVisible ? TRANSCRIPT_EXPANDED_HEIGHT : "0px",
  flex: "none",
  transitionDelay: isVisible ? "0ms" : `${TRANSCRIPT_COLLAPSE_DELAY_MS}ms`,
});

const transcriptRevealStyle = (isVisible: boolean): CSSProperties => ({
  opacity: isVisible ? 1 : 0,
  clipPath: isVisible ? "inset(0 0 0 0)" : "inset(100% 0 0 0)",
});

interface VideoPageProps {
  chapterId: number;
  lesson: Lesson;
  videoActivity: VideoActivity;
  onActivityFinished: () => void;
  onUpdateUserWords: (ds: UserVideoWord[]) => void;
  onTrackLearningAdded: (learningWords: number) => void;
  onTrackKnownAdded: (knownWords: number) => void;
  onTrackListeningTime: (time: number) => void;
  /**
   * Per-chapter display mode served by the backend
   * (domain.VideoDisplayMode: "" | "immersive" | "immersiveTranscript").
   * Immersive modes hide the captions/transcript (iOS VideoNew hideCaptions).
   */
  videoDisplayMode?: string;
  /**
   * Authoritative initial caption-drawer state, supplied by Flight School video
   * activities (iOS FlightSchoolVideoActivity.tsx:37 `setCaptionsExpanded(
   * data.defaultCaptionsExpanded ?? false)`). FS authors this per video:
   * Spanish-content clips set it `true`, while the English explainer videos
   * ("What are cognates?", "The CION rule", …) leave it undefined → captions
   * default OFF, since an English transcript is useless to a Spanish learner.
   * There is no general English/Spanish field on a video — this authored flag is
   * the only reliable language signal, and it only exists for Flight School.
   * Undefined (the regular lesson route) preserves the prior default.
   */
  defaultCaptionsExpanded?: boolean;
  /**
   * Suppresses the word/phrase-tap tutorial for this video, mirroring iOS's
   * `disabled` gate (videoTutorial.ts:31 `if (disabled …) return undefined`).
   * Regular lessons leave this undefined → the tutorial runs as before (iOS
   * runs it too: VideoNew mounts with no `disableTutorial`, falling through to
   * videoTutorial.ts:50-53). Flight School passes `!videoTutorialMode`
   * (FlightSchoolVideoActivity.tsx:67), so the English explainer clips — which
   * authored no `videoTutorialMode` — disable it; without this the tutorial
   * would force-expand their captions and show a useless English transcript.
   */
  disableTutorial?: boolean;
  /** Fired when the video can't be played (after the one-shot source fallback). */
  onVideoError?: () => void;
  /** Manual escape for a video that silently stalls (loads the poster but never
   *  plays, firing no error). Advances past the video to the next step. */
  onSkip?: () => void;
  /**
   * Overrides the Continue CTA label (iOS VideoNew `continueButtonLabel`,
   * default "Let's Practice"; Basic Phrases 1 authors "Continue").
   */
  continueButtonLabel?: string;
  /**
   * The phrase the phrase_highlight tutorial should target (iOS VideoNew
   * `phraseHighlightTutorialTargetPhrase`). Anchors the coachmark on that phrase.
   */
  phraseHighlightTutorialTargetPhrase?: string;
  /**
   * Video tutorial mode (iOS VideoNew `tutorialMode`). Only
   * "combined_word_phrase" runs the word→phrase tutorial sequence; regular
   * lessons leave it undefined and fall back to the settings-flag gating.
   */
  tutorialMode?: "combined_word_phrase";
  /**
   * Forces the wordlist OFF for the whole video flow (iOS VideoNew folds
   * `hideWordlist` into `wordlistFlag = useFeatureFlag("wordlist_in_feed") &&
   * !hideWordlist`). Flight School passes true so FS clips never show the
   * wordlist button or pop the wordlist drawer on Continue (iOS
   * FlightSchoolVideoActivity.tsx:83). Regular lessons leave it undefined.
   */
  hideWordlist?: boolean;
  /**
   * When set, renders the back chevron OVERLAID on the video (top-left) and
   * makes the Continue CTA compact — Flight School video activities pass this so
   * the chevron sits on the clip (iOS) instead of in a side margin, and the
   * "Let's Practice" button isn't a full-width bar. Regular lessons omit it.
   */
  onBack?: () => void;
}

const VideoPage = observer(function VideoPage({
  chapterId,
  lesson,
  videoActivity,
  onActivityFinished,
  onUpdateUserWords,
  onTrackKnownAdded,
  onTrackLearningAdded,
  onTrackListeningTime,
  videoDisplayMode,
  defaultCaptionsExpanded,
  disableTutorial,
  onVideoError,
  onSkip,
  continueButtonLabel,
  phraseHighlightTutorialTargetPhrase,
  tutorialMode,
  hideWordlist,
  onBack,
}: VideoPageProps) {
  const lessonId = lesson.lessonId;
  const posthog = usePostHog();
  // "You added this word to known" confirmation (iOS showKnownToast parity) —
  // word-tap saves in the lesson video panel were previously silent.
  const platform = getPlatform();
  const { hideCaptions } = getVideoPropsForDisplayMode(videoDisplayMode);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const { settings, updateSettings, isLoading: isUserSettingsLoading } =
    useUserSettings();

  const words = videoActivity.userVideoWords;
  const hasVideoWords = (words?.length ?? 0) > 0;

  // ---------------------------------------------------------------------------
  // Feed-state shim: the shared Scrubber/Transcript/BottomPanel plumbing
  // resolves the active clip via activeLessonData$ (chapters$ + lessonDataCache$).
  // Install a synthetic single-chapter "feed" for this lesson and restore the
  // real feed state on unmount (the feed and lesson routes never co-mount).
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const prev = {
      chapters: chapters$.peek(),
      index: feedCurrentIndex$.peek(),
      lessonIndex: lesson$.currentLessonIndex.peek(),
    };
    chapters$.set([
      {
        chapterID: chapterId,
        lessonIDs: [lessonId],
        dialect: "unknown",
        title: "",
        isLiked: false,
      },
    ]);
    feedCurrentIndex$.set(0);
    lesson$.currentLessonIndex.set(0);
    resetPlayerState();
    videoDuration$.set(0);
    return () => {
      chapters$.set(prev.chapters);
      feedCurrentIndex$.set(prev.index);
      lesson$.currentLessonIndex.set(prev.lessonIndex);
      resetPlayerState();
      videoDuration$.set(0);
    };
  }, [chapterId, lessonId]);

  // Keep the shared lesson-data cache in sync with the lesson route's data so
  // activeLessonData$ (and the per-second listen-time context) read this clip.
  useEffect(() => {
    lessonDataCache$[lessonId].set({
      lesson,
      video: videoActivity,
    } as GetLessonDataResponse);
  }, [lessonId, lesson, videoActivity]);

  // Captions default per lesson: expanded, unless the display mode is immersive
  // or the lesson has no words (iOS VideoNew auto-collapse, VideoNew.tsx:258-268).
  // Flight School videos additionally honour an authored caption default
  // (iOS FlightSchoolVideoActivity.tsx:37): the English explainer clips leave it
  // undefined → captions OFF, since an English transcript is useless to a Spanish
  // learner; Spanish-content FS clips set it true. The user can still toggle
  // captions back on. Regular lessons pass undefined and keep the prior default.
  //
  // Applied DURING render (not in an effect) so the very first paint already
  // reflects the authored default. An effect runs after the first commit, so a
  // clip that should start CLOSED would first paint OPEN (with the stale global
  // value), then the effect would flip it closed — and the height/clip-path CSS
  // transition would animate that shut, the visible "transcript animates closed
  // on open" flash on Flight School English clips. Seeding the observable
  // synchronously means `false` is the initial value, and CSS transitions never
  // fire on initial mount → it first-renders closed, exactly like iOS. The ref
  // guard makes this a one-shot per input change, so a user toggle isn't clobbered
  // on the next re-render.
  const captionInitSig = `${lessonId}|${hideCaptions}|${hasVideoWords}|${defaultCaptionsExpanded}`;
  const captionInitRef = useRef<string | null>(null);
  if (captionInitRef.current !== captionInitSig) {
    captionInitRef.current = captionInitSig;
    const authoredDefault = defaultCaptionsExpanded ?? true;
    captionsVisible$.set(!hideCaptions && hasVideoWords && authoredDefault);
  }

  // ---------------------------------------------------------------------------
  // Lesson-stats + lesson-cache sync: word status changes now flow through the
  // shared wordStatusOverrides$ (transcript tap / panel toggle / wordlist bulk).
  // Mirror them up to the lesson route (ModuleComplete stats + WordList filter).
  // ---------------------------------------------------------------------------
  const callbacksRef = useRef({
    onUpdateUserWords,
    onTrackKnownAdded,
    onTrackLearningAdded,
  });
  callbacksRef.current = {
    onUpdateUserWords,
    onTrackKnownAdded,
    onTrackLearningAdded,
  };
  const wordsRef = useRef(words);
  wordsRef.current = words;
  // While the wordlist modal is open, FeedWordlistModal already syncs dictionary
  // stats itself (addKnownWords) — skip per-word stat deltas to avoid doubling.
  const wordlistOpenRef = useRef(false);
  const markedDuringWordlistRef = useRef(false);

  useEffect(() => {
    const dispose = wordStatusOverrides$.onChange(({ value, changes }) => {
      const lessonWords = wordsRef.current || [];
      // Overrides are keyed by canonical wordKey now, so a word that occurs
      // multiple times in the lesson maps to ONE entry — stat deltas count the
      // unique word once (matches iOS dictionary counting), not per occurrence.
      const byKey = new Map(lessonWords.map((w) => [w.wordKey, w]));
      let touchedLessonWord = false;
      for (const change of changes) {
        const key = String(change.path[0]);
        const word = byKey.get(key);
        if (!word) continue;
        touchedLessonWord = true;
        const next = change.valueAtPath as UserWordStatus | undefined;
        // The PRIOR OPTIMISTIC OVERRIDE (undefined if this word had none this
        // session). Drives stat deltas off what THIS session actually credited
        // — not the word's load-time status — so we never over/undercount words
        // that loaded already learning/known, and we correctly reverse on a
        // rolled-back failed write.
        const prevOverride = change.prevAtPath as UserWordStatus | undefined;
        const displayStatus = prevOverride ?? word.status;
        if (next === displayStatus) continue;
        if (wordlistOpenRef.current) {
          markedDuringWordlistRef.current = true;
          continue;
        }
        const cb = callbacksRef.current;
        // Symmetric credit/debit on the session's optimistic override: leaving a
        // bucket debits the credit this session gave it; entering one credits.
        // This makes new→learning→known count once, and a failed-write rollback
        // (e.g. known→new) reverse the optimistic credit.
        if (prevOverride === "known") cb.onTrackKnownAdded(-1);
        else if (prevOverride === "learning") cb.onTrackLearningAdded(-1);
        if (next === "known") cb.onTrackKnownAdded(1);
        else if (next === "learning") cb.onTrackLearningAdded(1);
      }
      if (!touchedLessonWord) return;
      const updated = lessonWords.map((w) => {
        const override = value[w.wordKey];
        return override && override !== w.status
          ? { ...w, status: override }
          : w;
      });
      callbacksRef.current.onUpdateUserWords(updated);
    });
    return dispose;
  }, []);

  // Listening time → lesson stats (ModuleComplete): the shared player increments
  // dailyElapsedSeconds$ once per playing second and posts listen time itself.
  useEffect(() => {
    let prev = dailyElapsedSeconds$.peek();
    const dispose = dailyElapsedSeconds$.onChange(({ value }) => {
      const delta = value - prev;
      prev = value;
      if (delta > 0) onTrackListeningTime(delta);
    });
    return dispose;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------------------------------------------------------------------------
  // Word/phrase tutorial — driven by the iOS settings flags
  // (VideoNew.tsx:143-194 + lib/videoTutorial.ts): word_click while
  // wordClickTutorialComplete is false, then phrase_highlight while
  // phraseHighlightTutorialComplete is false; one phase per lesson.
  // ---------------------------------------------------------------------------
  const [tutorialDoneThisLesson, setTutorialDoneThisLesson] = useState(false);
  const [recapVisible, setRecapVisible] = useState(false);
  // When the learner completes word_click THIS session, the combined-tutorial
  // phrase_highlight phase is HELD until the word-status recap is dismissed + a
  // ~10s delay (iOS allowCombinedPhraseHighlight) so it never overlaps the recap
  // modal. Default false (not held) so a learner who already finished word_click
  // in a PRIOR session — and therefore sees no recap now — still gets the phrase
  // coachmark instead of being stuck.
  const [combinedPhraseHeld, setCombinedPhraseHeld] = useState(false);
  const phraseHighlightDelayRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const tutorialPhase: LessonTutorialPhase | undefined = useMemo(() => {
    // iOS `disabled` gate (videoTutorial.ts:31): Flight School disables the
    // tutorial on clips that didn't author `videoTutorialMode` — i.e. the
    // English explainer clips. Without this the tutorial fired on every video
    // with words for any user who hadn't finished it (the whole new-FS audience)
    // and force-expanded captions (see the effect below), overriding the
    // authored caption-OFF default and showing a useless English transcript.
    // Regular lessons leave `disableTutorial` undefined, so the tutorial still
    // runs there exactly as before (and as on iOS).
    if (disableTutorial) return undefined;
    if (isUserSettingsLoading || !settings) return undefined;
    if (tutorialDoneThisLesson || !hasVideoWords || hideCaptions)
      return undefined;
    if (!settings.wordClickTutorialComplete) return "word_click";
    // iOS only runs the phrase-highlight phase in the combined tutorial mode
    // (VideoNew.tsx:147-149). Regular lessons leave `tutorialMode` undefined and
    // historically ran phrase_highlight off the settings flag alone — preserve
    // that by only gating on tutorialMode when it is explicitly supplied (FS).
    if (!settings.phraseHighlightTutorialComplete) {
      if (tutorialMode && tutorialMode !== "combined_word_phrase")
        return undefined;
      // In the combined FS tutorial, while word_click was completed THIS session
      // phrase_highlight is held until the recap is dismissed + the delay — until
      // then the recap modal owns the screen (iOS VideoNew sequencing).
      if (tutorialMode === "combined_word_phrase" && combinedPhraseHeld)
        return undefined;
      return "phrase_highlight";
    }
    return undefined;
  }, [
    disableTutorial,
    isUserSettingsLoading,
    settings,
    tutorialDoneThisLesson,
    hasVideoWords,
    hideCaptions,
    tutorialMode,
    combinedPhraseHeld,
  ]);

  // Cancel the pending combined-phrase-highlight unlock timer on unmount.
  useEffect(
    () => () => {
      if (phraseHighlightDelayRef.current)
        clearTimeout(phraseHighlightDelayRef.current);
    },
    [],
  );

  // Force captions on while the tutorial is showing (iOS setCaptionsExpanded(true)).
  useEffect(() => {
    if (tutorialPhase) captionsVisible$.set(true);
  }, [tutorialPhase]);

  const handleTutorialCompleted = useCallback(
    (phase: LessonTutorialPhase) => {
      // In the combined Flight-School tutorial, word_click must NOT end the
      // tutorial for this clip — phrase_highlight follows in the SAME lesson
      // (iOS VideoNew only sets its per-lesson "done" ref outside combined mode,
      // VideoNew.tsx handleTutorialCompleted). Regular lessons keep the
      // historical one-phase-per-lesson behaviour so their flow is unchanged.
      if (!(tutorialMode === "combined_word_phrase" && phase === "word_click")) {
        setTutorialDoneThisLesson(true);
      }
      // iOS useTrackDemoPhaseCompletion: per-phase event + generic aggregate.
      if (phase === "word_click") {
        posthog?.capture("tutorial_word_click_completed");
      } else {
        posthog?.capture("tutorial_phrase_highlight_completed");
      }
      posthog?.capture("demo_phase_completed", { phase });
      if (phase === "word_click") {
        updateSettings({ wordClickTutorialComplete: true }).catch((error) => {
          console.error("Failed to update tutorial settings:", error);
        });
        // Completed word_click this session → hold phrase_highlight behind the
        // recap (released on recap-dismiss + delay below).
        if (tutorialMode === "combined_word_phrase") setCombinedPhraseHeld(true);
        setRecapVisible(true);
      } else {
        updateSettings({
          phraseHighlightTutorialComplete: true,
          showWordTutorialNextLesson: false,
        }).catch((error) => {
          console.error("Failed to update tutorial settings:", error);
        });
      }
    },
    [posthog, updateSettings, tutorialMode]
  );

  // The phase completes when the user performs the real gesture: a word panel
  // opening completes word_click; a finalized phrase selection completes
  // phrase_highlight (iOS completes on the native gesture the same way).
  const panelMode = panelMode$.get();
  useEffect(() => {
    if (!tutorialPhase) return;
    if (tutorialPhase === "word_click" && panelMode === "word") {
      handleTutorialCompleted("word_click");
    } else if (
      tutorialPhase === "phrase_highlight" &&
      panelMode === "sentence"
    ) {
      handleTutorialCompleted("phrase_highlight");
    }
  }, [panelMode, tutorialPhase, handleTutorialCompleted]);

  // ---------------------------------------------------------------------------
  // wordlist_in_feed flag flow (iOS VideoNew.tsx:197-252): wordlist button on
  // the video screen + auto-open drawer on "Let's Practice" when unreviewed new
  // words exist and the video is at/above appConfig.wordlistMinLevel.
  // ---------------------------------------------------------------------------
  const wordlistFlag =
    !!useFeatureFlagEnabled("wordlist_in_feed") && !hideWordlist;
  const [showWordlistModal, setShowWordlistModal] = useState(false);
  const hasShownWordlistNudge = useRef(false);
  const openedFromLetsPractice = useRef(false);
  wordlistOpenRef.current = showWordlistModal;

  const { data: appConfig } = useQuery<AppConfigResponse>({
    queryKey: ["appConfig"],
    queryFn: async () => {
      // API_BASE_URL is "/" in dev (same-origin Vite proxy) — join without
      // doubling the slash, which the browser would read as protocol-relative.
      const base = API_BASE_URL.endsWith("/")
        ? API_BASE_URL.slice(0, -1)
        : API_BASE_URL;
      const res = await fetch(`${base}/public/app-config`);
      if (!res.ok) {
        throw new Error(`Failed to fetch app config: ${res.status}`);
      }
      return res.json();
    },
    staleTime: 10 * 60 * 1000,
    retry: 2,
  });

  const chapterInfoQuery = useChapterInfo(chapterId, chapterId > 0);
  const videoLevelText = chapterInfoQuery.data?.videoLevelText;
  const isAboveMinLevel = useMemo(() => {
    const minLevel = appConfig?.wordlistMinLevel || "No Level";
    const minLevelIndex = LEVEL_ORDER.indexOf(minLevel);
    const videoLevelIndex = LEVEL_ORDER.indexOf(videoLevelText || "No Level");
    return videoLevelIndex >= minLevelIndex;
  }, [appConfig?.wordlistMinLevel, videoLevelText]);

  // Same logic as iOS hasUnreviewedWords (VideoNew.tsx:209-218), with the
  // session's optimistic overrides standing in for globalStore statuses.
  const overrides = wordStatusOverrides$.get();
  const hasUnreviewedWords = useMemo(() => {
    const seen = new Set<string>();
    return (words || []).some((word) => {
      if (seen.has(word.wordKey)) return false;
      seen.add(word.wordKey);
      if (word.status !== "new") return false;
      const override = overrides[word.wordKey];
      if (override === "learning" || override === "known") return false;
      if (isInvalidDictionaryWord(word.displayText)) return false;
      return true;
    });
  }, [words, overrides]);

  // ---------------------------------------------------------------------------
  // Pause gating: tutorial / recap / wordlist drawer pause playback, mirroring
  // iOS effectivePaused (VideoNew.tsx:409-414).
  // ---------------------------------------------------------------------------
  const blocking = !!tutorialPhase || recapVisible || showWordlistModal;
  const prevBlockingRef = useRef(false);
  useEffect(() => {
    if (blocking) {
      isPaused$.set(true);
    } else if (prevBlockingRef.current && panelMode$.peek() === null) {
      isPaused$.set(false);
    }
    prevBlockingRef.current = blocking;
  }, [blocking]);

  // "Let's Practice" (iOS handleLetsPractice): nudge the wordlist drawer once
  // when the flag is on, unreviewed new words exist and the level qualifies.
  const handleContinueClick = () => {
    if (isTransitioning) return;
    if (
      wordlistFlag &&
      hasUnreviewedWords &&
      isAboveMinLevel &&
      !hasShownWordlistNudge.current
    ) {
      hasShownWordlistNudge.current = true;
      openedFromLetsPractice.current = true;
      markedDuringWordlistRef.current = false;
      setShowWordlistModal(true);
      return;
    }
    setIsTransitioning(true);
    onActivityFinished();
  };

  const handleWordlistPressed = () => {
    openedFromLetsPractice.current = false;
    markedDuringWordlistRef.current = false;
    setShowWordlistModal(true);
  };

  const handleWordlistClose = () => {
    setShowWordlistModal(false);
    // iOS onComplete: a successful bulk-mark from the Let's Practice nudge
    // advances to practice; a plain dismissal stays on the video.
    if (
      openedFromLetsPractice.current &&
      markedDuringWordlistRef.current &&
      !isTransitioning
    ) {
      setIsTransitioning(true);
      onActivityFinished();
    }
  };

  /** Toggle the selected word's "known" status (check in the word panel).
   * Shared with the feed transcript/panel via the canonical helper; the
   * confirmation toast is the app-level FeedKnownWordToast (wordKnownToast$). */
  const handleToggleWordKnown = useCallback(() => {
    const word = selectedWord$.peek();
    if (!word) return;
    toggleWordKnown(word, lessonId);
  }, [lessonId]);

  const isCaptionsVisible = captionsVisible$.get() && !hideCaptions;
  // Prefer the REAL decoded aspect (reported by the player, iOS presentationSize
  // parity) over the unreliable `aspectRatio` string; portrait → fill + overlay,
  // landscape → letterbox band + transcript below. Reset per lesson via a
  // render-time guard (no effect).
  const [realAspectW, setRealAspectW] = useState<number | null>(null);
  // YouTube draws its own centre play button — hide ours for YouTube so they
  // don't overlap (only the bare Cloudflare <video> needs CenterPlayPause).
  const [activeSource, setActiveSource] = useState<
    "youtube" | "cloudflare" | null
  >(null);
  const aspectLessonRef = useRef<number | null>(null);
  if (aspectLessonRef.current !== lessonId) {
    aspectLessonRef.current = lessonId;
    if (realAspectW !== null) setRealAspectW(null);
    if (activeSource !== null) setActiveSource(null);
  }
  // Same fallback as the feed (VideoLayout) for parity: prefer the real decoded
  // aspect; before that, use the source the clip WILL play from (resolved
  // synchronously, so no null window flashes the wrong column) — trust the
  // metadata for YouTube (can't be measured; portrait only when explicitly
  // "9:16"), and default Cloudflare to the portrait column (metadata mislabels
  // portrait clips as "16:9"). The <video> reports the real aspect shortly.
  const resolvedSource = activeSource ?? resolveVideoSource(videoActivity);
  const isPortraitVideo =
    realAspectW != null
      ? realAspectW < 1
      : resolvedSource === "youtube"
        ? videoActivity.aspectRatio === "9:16"
        : true;
  const wordForPanel = selectedWord$.get();
  const phraseGloss =
    panelMode === "sentence" ? selectedPhraseGloss$.get() : undefined;

  return (
    // self-stretch + min-h-0: the lesson/flight-school shells center this in an
    // `items-center` flex parent, which would otherwise shrink the player to its
    // intrinsic (portrait) height and push the captions + "Let's Practice" button
    // below the fold on wide/tall desktops. Stretching to the parent's full
    // height lets the inner flex-1 stages bound the video to the viewport so a
    // portrait clip + captions + button all fit with no scroll.
    <div className="relative h-full w-full min-h-0 self-stretch flex flex-col items-center bg-black">
      <div className="w-full flex-1 min-h-0 flex justify-center">
        <div className="flex flex-col flex-1 min-h-0 px-0 py-3">
          {(() => {
            // Same orientation rule as the feed: portrait clips fill a 9:16
            // column with the transcript OVERLAID; landscape clips letterbox in a
            // 16:9 band with the transcript in a strip BELOW.
            const media = (
              <FeedVideoPlayer
                videoActivity={videoActivity}
                lessonId={lessonId}
                chapterId={chapterId}
                onUnrecoverableError={onVideoError}
                showPoster
                onVideoAspect={setRealAspectW}
                onSourceResolved={(s) => {
                  setActiveSource(s);
                  // Drop a stale Cloudflare-decoded aspect on CF→YouTube
                  // fallback (YouTube can't be measured; use metadata instead).
                  if (s === "youtube") setRealAspectW(null);
                }}
              />
            );
            const controls = (
              <>
                {activeSource === "cloudflare" && <CenterPlayPause />}
                {/* Right rail — raised up the frame (TikTok/Reels style) on the
                    full-bleed portrait layout; band bottom on landscape. */}
                <div
                  className={`absolute right-0 z-20 pr-2 ${
                    isPortraitVideo ? "bottom-[56px]" : "bottom-0 pb-2"
                  }`}
                >
                  <LessonActionBar
                    showWordlistButton={wordlistFlag}
                    onWordlistPressed={handleWordlistPressed}
                    showCaptionsToggle={!hideCaptions && hasVideoWords}
                  />
                </div>
                {panelMode && (
                  <div
                    className="absolute bottom-0 left-0 right-0 z-20 flex items-end pointer-events-none"
                    style={{ height: "25%" }}
                  >
                    <div
                      className="w-full pb-4 pointer-events-auto"
                      style={{ maxWidth: "calc(100% - 56px)" }}
                    >
                      <BottomPanel
                        mode={panelMode}
                        word={wordForPanel}
                        translation={phraseGloss}
                        lessonId={lessonId}
                        chapterId={chapterId}
                        onSave={handleToggleWordKnown}
                        onClose={handlePanelClose}
                      />
                    </div>
                  </div>
                )}
                <Scrubber overlay />
              </>
            );
            const transcriptInner = (
              <Transcript
                words={words}
                videoId={videoActivity.youtubeId}
                lessonId={lessonId}
                overlayMode={isPortraitVideo}
              />
            );

            // ONE video frame whose className just toggles (portrait column vs
            // letterboxed band), so resolving the real aspect mid-load resizes the
            // player in place instead of remounting + restarting the video.
            return (
              <div className="flex-1 min-h-0 w-full flex flex-col items-center justify-center">
                <div
                  className={
                    isPortraitVideo
                      ? "relative mx-auto h-full max-h-full aspect-[9/16] max-w-full overflow-hidden bg-black shrink-0"
                      : `relative w-full mx-auto ${VIDEO_MAX_W} max-h-full aspect-[16/9] overflow-hidden bg-black shrink-0`
                  }
                >
                  {media}
                  {isPortraitVideo && !hideCaptions && (
                    <div
                      className="absolute left-0 z-10 overflow-hidden transition-[height] duration-300 ease-in-out"
                      style={{ ...transcriptCollapseStyle(isCaptionsVisible), bottom: 84, right: 56 }}
                    >
                      <div
                        className="h-full overflow-hidden transition-[opacity,clip-path] duration-300 ease-in-out"
                        style={transcriptRevealStyle(isCaptionsVisible)}
                      >
                        {transcriptInner}
                      </div>
                    </div>
                  )}
                  {controls}
                  {onBack && (
                    <div
                      className="absolute top-2 left-2 z-20"
                      style={{ filter: "drop-shadow(0 1px 4px rgba(0,0,0,0.5))" }}
                    >
                      <BackButton onPress={onBack} color="#fff" />
                    </div>
                  )}
                </div>
                {!isPortraitVideo && !hideCaptions && (
                  <div
                    className={`relative w-full mx-auto ${VIDEO_MAX_W} overflow-hidden transition-[height] duration-300 ease-in-out shrink-0`}
                    style={transcriptCollapseStyle(isCaptionsVisible)}
                  >
                    <div
                      className="h-full overflow-hidden transition-[opacity,clip-path] duration-300 ease-in-out"
                      style={transcriptRevealStyle(isCaptionsVisible)}
                    >
                      {transcriptInner}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      </div>

      {/* "Let's Practice" CTA (the scrubber now overlays the video bottom). */}
      <div className={`w-full shrink-0 px-4 ${VIDEO_MAX_W}`}>
        <div
          className={`flex justify-center items-center ${
            platform === "web" ? "bottom-padding-web" : "pb-6"
          }`}
        >
          {/* Flight School (onBack) keeps a compact, centered CTA rather than the
              full-width lesson bar. */}
          <div className={onBack ? "w-full max-w-[380px]" : "w-full"}>
            <ContinueButton
              onClick={handleContinueClick}
              text={continueButtonLabel ?? "Let's Practice"}
              disabled={isTransitioning}
              // iOS parity: the "Let's Practice" button uses the "dark" variant
              // (#3A3A3C gray + white text), not the default blue. See
              // client-native ContinueButton variantMap variant="dark".
              backgroundColor="#3A3A3C"
              textColor="white"
            />
          </div>
        </div>
        {onSkip && (
          <div className="flex justify-center pb-3">
            <button
              type="button"
              onClick={onSkip}
              className="text-white/50 hover:text-white/80 text-sm font-medium px-2 py-1 transition-colors"
            >
              Video not loading? Skip →
            </button>
          </div>
        )}
      </div>

      {tutorialPhase && (
        // `targetPhrase` is threaded for caller-compat (iOS VideoNew
        // `phraseHighlightTutorialTargetPhrase`) but the web coachmark always
        // spotlights the first transcript line, so it isn't shown in the copy.
        <LessonTutorialOverlay
          phase={tutorialPhase}
          targetPhrase={
            tutorialPhase === "phrase_highlight"
              ? phraseHighlightTutorialTargetPhrase
              : undefined
          }
        />
      )}

      <WordStatesRecapModal
        isVisible={recapVisible}
        onContinue={() => {
          setRecapVisible(false);
          // iOS: after the recap is dismissed, wait ~10s before RELEASING the
          // held phrase-highlight coachmark (COMBINED_TUTORIAL_PHRASE_DELAY).
          if (tutorialMode === "combined_word_phrase") {
            if (phraseHighlightDelayRef.current)
              clearTimeout(phraseHighlightDelayRef.current);
            phraseHighlightDelayRef.current = setTimeout(
              () => setCombinedPhraseHeld(false),
              10000,
            );
          }
        }}
      />

      {showWordlistModal && (
        <FeedWordlistModal
          lessonId={lessonId}
          words={words || []}
          onClose={handleWordlistClose}
          onComplete={() => {
            // Explicit success signal (iOS FeedWordlistModal onComplete) —
            // backs up the wordStatusOverrides$ inference above.
            markedDuringWordlistRef.current = true;
          }}
        />
      )}
    </div>
  );
});

export default VideoPage;
